import { useFormik } from "formik";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "@hubertai/hubert-component-library";
import { requestPasswordReset, signInWithPassword } from "../../behavior/api/auth";
import { clearTokenFromStorage, getAccessToken, storeTokens } from "../../behavior/security/auth.service";
import type { TPageState } from "../../constants";
import { ApplicationError, PageRoute, PageState, ProviderId, Providers } from "../../constants";
import type { Invitation } from "../../types/invitation";
import { loginFormSchema } from "../../utils/validationSchemas";
import { Button } from "../elements";
import type { LargeAlertProps } from "../elements/LargeAlert";
import { Password, Textbox } from "../forms";

interface LoginFormProps {
    setShowAlert: Dispatch<SetStateAction<LargeAlertProps>>;
    preLoadedEmail?: string | null;
    state?: TPageState | null;
    validateProvider?: (selectedProvider: string) => boolean;
    invitation?: Invitation | null;
    postLoginEvent?: (token: string, displayName: string) => void;
}

const LoginForm = ({
    setShowAlert,
    preLoadedEmail,
    state,
    validateProvider = () => true,
    postLoginEvent = () => null,
}: LoginFormProps) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const accessToken = getAccessToken();

    const initialValues = {
        email: preLoadedEmail || "",
        password: "",
    };

    const getProviderById = (providerId: string) => {
        switch (providerId) {
            case ProviderId.GOOGLE:
                return Providers.GOOGLE.toString();
            default:
                return "";
        }
    };

    const {
        values,
        errors,
        touched,
        handleSubmit,
        handleChange,
        handleBlur,
        setFieldValue,
        getFieldHelpers,
        getFieldMeta,
    } = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema: loginFormSchema,
        onSubmit: async (props) => {
            if (validateProvider && validateProvider(ProviderId.PASSWORD)) {
                setLoading(true);
                try {
                    const response = await signInWithPassword(props!.email, props!.password);

                    if (!response) {
                        toast.error(t("AppMessages.server-error"));
                        return;
                    }
                    if (response.isError && response.errorMessage) {
                        if (response.errorMessage === ApplicationError.EMAIL_NOT_VERIFIED) {
                            setShowAlert({
                                title: t(`AppMessages.${response.errorMessage}-title`),
                                subtitle: t(`AppMessages.${response.errorMessage}-subtitle`),
                                type: "warning",
                            });
                            return;
                        }
                        toast.error(t(`AppMessages.${response.errorMessage}`));
                    } else if (response.payload?.access_token) {
                        storeTokens(response.payload);

                        if (
                            postLoginEvent &&
                            (state === PageState.INVITATION_SIGN_IN ||
                                state === PageState.JOB_APPLY_PRE_INTERVIEW_SIGN_IN)
                        ) {
                            postLoginEvent(response.payload.access_token, props!.email);
                            return;
                        }

                        if (state === PageState.VERIFIED_EMAIL) {
                            navigate(PageRoute.TRANSITION_PAGE, {
                                replace: true,
                                state: {
                                    redirectUrl: PageRoute.DASHBOARD,
                                    emailVerified: true,
                                },
                            });
                            return;
                        }

                        let redirectUrl = PageRoute.DASHBOARD.toString();

                        if (
                            state === PageState.JOB_APPLY_POST_INTERVIEW_SIGN_IN ||
                            state === PageState.JOB_APPLY_POST_INTERVIEW_SIGN_UP
                        ) {
                            redirectUrl = redirectUrl.concat(`?state=${PageState.INTERVIEW_COMPLETED}`);
                        }

                        const redirectParam = searchParams.get("redirectUrl");
                        if (redirectParam) {
                            redirectUrl = redirectParam;
                        }

                        navigate(PageRoute.TRANSITION_PAGE, {
                            replace: true,
                            state: { redirectUrl },
                        });
                    } else {
                        toast.error(t("AppMessages.invalid-credentials"));
                    }
                } catch (e) {
                    const message = e instanceof Error ? e.message : "Unknown error";
                    toast.error(message);
                } finally {
                    setLoading(false);
                }
            }
        },
    });

    useEffect(() => {
        const userEmail = searchParams.get("email");
        if (state === PageState.PASSWORD_RESET && userEmail) {
            setFieldValue("email", userEmail);
            toast.success(t("AppMessages.password-reset"));
            toast.info(t("AppMessages.re-enter-new-password"));
        }
    }, [searchParams, setFieldValue, t, state]);

    useEffect(() => {
        if (state === PageState.VERIFIED_EMAIL) {
            toast.success(t("AppMessages.email-verified"));
            toast.info(t("AppMessages.re-enter-password"));
        } else if (state === PageState.EMAIL_PREVIOUSLY_VERIFIED) {
            toast.info(t("AppMessages.re-enter-password"));
        }
    }, [state, t]);

    useEffect(() => {
        if (state === PageState.SESSION_EXPIRED) {
            clearTokenFromStorage();
        }
    }, [state]);

    useEffect(() => {
        if (preLoadedEmail) setFieldValue("email", preLoadedEmail);
    }, [setFieldValue, preLoadedEmail]);

    useEffect(() => {
        if (
            accessToken &&
            state !== PageState.JOB_APPLY_PRE_INTERVIEW_SIGN_IN &&
            state !== PageState.INVITATION_SIGN_IN
        ) {
            navigate(PageRoute.TRANSITION_PAGE, {
                replace: true,
                state: { redirectUrl: PageRoute.DASHBOARD },
            });
        }
    }, [state, accessToken, navigate]);

    const initiatePasswordReset = async () => {
        const fieldName = "email";
        const emailMeta = getFieldMeta(fieldName);
        if (!emailMeta.error && emailMeta.value) {
            setResetLoading(true);
            try {
                const redirectUrl =
                    state === PageState.INVITATION_SIGN_IN || state === PageState.JOB_APPLY_PRE_INTERVIEW_SIGN_IN
                        ? window.location.href
                        : undefined;

                const response = await requestPasswordReset({
                    email: emailMeta.value,
                    redirectUrl,
                });

                if ("type" in response && response.type === "PASSWORD_NOT_SET") {
                    const { message } = response;
                    toast.warning(`${t("ForgotPassword.PasswordNotSetAlert_Message")} ${getProviderById(message)}`);
                    return;
                }

                setShowAlert((prevState) => ({
                    ...prevState,
                    title: t("ForgotPassword.ResetLinkAlert_Title"),
                    subtitle: t("ForgotPassword.ResetLinkAlert_Subtitle"),
                }));
            } catch (e) {
                const message = e instanceof Error ? e.message : "Unknown error";
                toast.error(message);
            } finally {
                setResetLoading(false);
            }
        } else {
            getFieldHelpers(fieldName).setTouched(true);
        }
    };

    return (
        <form className="login-form" onSubmit={handleSubmit}>
            <Textbox
                id="email"
                label={t("Login.Label_Email")}
                name="email"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.email}
                error={errors.email}
                touched={touched.email}
                disabled={!!preLoadedEmail}
            />
            <Password
                id="password"
                label={t("Login.Label_Password")}
                name="password"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.password}
                error={errors.password}
                touched={touched.password}
            />
            <Button disabled={loading || resetLoading} fullWidth submit className="mt-2">
                {t("Login.ButtonText_SignIn")}
            </Button>
            <div className="forgot-password-link-wrapper">
                <Button link secondary disabled={loading || resetLoading} onClick={initiatePasswordReset}>
                    {t("Login.ButtonText_ForgotPassword")}
                </Button>
            </div>
        </form>
    );
};

export default LoginForm;
